import {
  BLOCK_CATEGORIES,
  BLOCK_CATEGORY_LABELS,
  type BlockCategory,
  blockSearchTerms,
  blockTokens,
  buildBlock,
  DEFAULT_BLOCK_MS,
  LIBRARY_BLOCKS,
  type LibraryBlock,
  type LibraryItem,
  pageSize,
  placeSavedElements,
  searchList,
  videoDuration,
} from "@cva/shared";
import { useQuery } from "@tanstack/react-query";
import { Blocks, Maximize2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { ConfirmDialog } from "../components/ui/confirm";
import { ContextMenu } from "../components/ui/context-menu";
import { DropdownItem, DropdownSeparator } from "../components/ui/dropdown";
import { orpc } from "../lib/api";
import { canvasMeasure, tokenFaces, useFontsReady } from "../lib/text-measure";
import { cn } from "../lib/utils";
import { BlocksBrowser } from "./BlocksBrowser";
import { LibraryBlockTile, SavedBlockTile } from "./block-tiles";
import { addPlacedElements } from "./commands";
import { useEnv } from "./env";
import { useLibraryActions, useLibraryItems } from "./library";
import { NameDialog } from "./NameDialog";
import { PanelTitle } from "./PanelTitle";
import { useEditor, useEditorApi } from "./store";
import { useVideoApi } from "./video/store";

/**
 * Building blocks ("Bausteine"): lower thirds, titles, intros, social
 * elements, cards, figures, quotes, calls to action, notices, stickers,
 * frames and collages in the brand, plus the organization's saved blocks.
 * A click inserts one as a group – in videos at the playhead, with its
 * animations; hovering a tile plays them. "Groß ansehen" opens all of them
 * in a fullscreen browser (`BlocksBrowser`).
 */

export type BlockFilter = "all" | "mine" | BlockCategory;

const TILE_WIDTH = 124;
const TILE_HEIGHT = 96;

/**
 * Built-in blocks of this kind of file and the saved ones, with previews and
 * the insert actions (blocks panel and ⌘K search).
 */
export function useBlockActions() {
  const store = useEditorApi();
  const env = useEnv();
  const video = useVideoApi();
  const kind = useEditor((s) => s.doc.kind);
  const width = useEditor((s) => pageSize(s.doc, s.doc.pages[s.pageIndex]).width);
  const height = useEditor((s) => pageSize(s.doc, s.doc.pages[s.pageIndex]).height);
  const brand = useEditor((s) => s.brand);
  const saved = useLibraryItems(env.orgId, "element");
  const org = useQuery(orpc.orgs.get.queryOptions({ input: { orgId: env.orgId } }));
  const frame = useMemo(() => ({ width, height }), [width, height]);
  const blocks = useMemo(
    () => LIBRARY_BLOCKS.filter((block) => !block.only || block.only === kind),
    [kind],
  );
  // Exact text widths once the brand fonts are loaded (like the template library).
  const faces = useMemo(
    () => tokenFaces(brand ?? blockTokens(LIBRARY_BLOCKS[0]!, { tokens: null })),
    [brand],
  );
  const fontsReady = useFontsReady(faces);
  const buildOptions = useMemo(
    () => ({
      tokens: brand,
      logos: env.brandKit?.logos ?? [],
      brandName: org.data?.name,
      measure: fontsReady ? canvasMeasure : undefined,
      frame,
      kind,
    }),
    [brand, env.brandKit?.logos, org.data?.name, fontsReady, frame, kind],
  );
  /** Start in videos: the playhead, early enough that the block fits into the video. */
  const startAt = (duration: number) => {
    if (!video) return 0;
    const t = Math.round(video.getState().time);
    const length = videoDuration(store.getState().doc);
    return length > duration ? Math.max(0, Math.min(t, length - duration)) : 0;
  };
  const insertBlock = (block: LibraryBlock) => {
    const duration = block.duration ?? DEFAULT_BLOCK_MS;
    const at = startAt(duration);
    addPlacedElements(store, buildBlock(block, { ...buildOptions, at }));
    if (video && Math.abs(video.getState().time - at) > 1) video.getState().seek(at);
  };
  const insertSaved = (item: Extract<LibraryItem, { kind: "element" }>) => {
    const at = startAt(item.data.duration ?? DEFAULT_BLOCK_MS);
    addPlacedElements(store, placeSavedElements(item.data, { ...frame, kind }, at));
  };
  return { blocks, saved, buildOptions, frame, kind, insertBlock, insertSaved };
}

/** Search fields of a built-in block. */
export const blockSearchFields = blockSearchTerms;

export function BlocksPanel() {
  const env = useEnv();
  const readOnly = useEditor((s) => s.readOnly);
  const [filter, setFilter] = useState<BlockFilter>("all");
  const [browsing, setBrowsing] = useState(false);
  const [query, setQuery] = useState("");
  const [renaming, setRenaming] = useState<LibraryItem | null>(null);
  const [deleting, setDeleting] = useState<LibraryItem | null>(null);
  const actions = useLibraryActions(env.orgId);
  const {
    blocks: allBlocks,
    saved,
    buildOptions,
    frame,
    kind,
    insertBlock,
    insertSaved,
  } = useBlockActions();
  const blocks = useMemo(() => searchList(allBlocks, query, blockSearchFields), [allBlocks, query]);
  const savedItems = useMemo(
    () => searchList(saved.items, query, (item) => [item.name]),
    [saved.items, query],
  );
  if (readOnly) {
    return (
      <p className="p-4 text-sm text-zinc-500">Im Ansichtsmodus lässt sich nichts einfügen.</p>
    );
  }

  const categories = BLOCK_CATEGORIES.filter((category) =>
    allBlocks.some((block) => block.category === category),
  );
  const found = BLOCK_CATEGORIES.filter((category) =>
    blocks.some((block) => block.category === category),
  );
  const shown = (category: BlockCategory) => filter === "all" || filter === category;

  return (
    <>
      <PanelTitle
        actions={
          <button
            type="button"
            onClick={() => setBrowsing(true)}
            title="Alle Bausteine groß ansehen"
            aria-label="Alle Bausteine groß ansehen"
            className="flex size-8 shrink-0 items-center justify-center rounded-md text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900"
          >
            <Maximize2 className="size-4" />
          </button>
        }
      >
        Bausteine
      </PanelTitle>
      <div className="space-y-2 border-b border-zinc-100 p-3">
        <div className="relative">
          <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-zinc-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Bausteine durchsuchen"
            aria-label="Bausteine durchsuchen"
            className="h-8 w-full rounded-lg border border-zinc-200 pr-2 pl-8 text-sm focus:border-brand-500 focus:outline-none"
          />
        </div>
        {/* Two chips and a category menu: 14 chips wrapped over four rows. */}
        <div className="flex items-center gap-1">
          {(["all", "mine"] as BlockFilter[]).map((entry) => (
            <button
              key={entry}
              type="button"
              onClick={() => setFilter(entry)}
              className={cn(
                "shrink-0 rounded-full border px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100",
                filter === entry
                  ? "border-zinc-900 bg-zinc-900 text-white hover:bg-zinc-900"
                  : "border-zinc-200",
              )}
            >
              {entry === "all" ? "Alle" : "Meine"}
            </button>
          ))}
          <select
            value={filter === "all" || filter === "mine" ? "" : filter}
            onChange={(event) => setFilter((event.target.value || "all") as BlockFilter)}
            aria-label="Kategorie"
            className={cn(
              "h-[26px] min-w-0 flex-1 rounded-full border px-2 text-xs font-medium focus:border-brand-500 focus:outline-none",
              filter === "all" || filter === "mine"
                ? "border-zinc-200 bg-white text-zinc-600"
                : "border-zinc-900 bg-zinc-900 text-white",
            )}
          >
            <option value="">Kategorie …</option>
            {categories.map((category) => (
              <option key={category} value={category}>
                {BLOCK_CATEGORY_LABELS[category]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto p-3">
        <p className="text-xs text-zinc-500">
          {kind === "video"
            ? "Klick fügt den Baustein am Abspielkopf ein – animiert und als Gruppe, die sich als Ganzes verschieben lässt. Zum Anschauen mit der Maus darüberfahren."
            : "Klick fügt den Baustein als Gruppe ein. Doppelklick auf die Gruppe wählt einzelne Teile, z. B. zum Ändern der Texte."}
        </p>
        {query.trim() && blocks.length === 0 && savedItems.length === 0 ? (
          <p className="rounded-lg bg-zinc-50 p-3 text-xs text-zinc-600">
            Kein Baustein passt zu „{query.trim()}“.
          </p>
        ) : null}
        {(filter === "all" || filter === "mine") && (!query.trim() || savedItems.length > 0) ? (
          <section className="space-y-2">
            <h3 className="text-xs font-semibold text-zinc-700">Meine Bausteine</h3>
            {savedItems.length === 0 ? (
              <div className="flex items-start gap-2 rounded-lg bg-zinc-50 p-3 text-xs text-zinc-600">
                <Blocks className="mt-0.5 size-4 shrink-0 text-zinc-400" />
                <span>
                  Eigene Bausteine speicherst du, indem du Elemente auswählst und{" "}
                  <strong>„Als Baustein speichern“</strong> wählst (rechte Maustaste oder rechts im
                  Panel). Sie stehen dann allen im Team hier zur Verfügung.
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {savedItems.map((item) => (
                  <ContextMenu
                    key={item.id}
                    menu={
                      <>
                        <DropdownItem onSelect={() => insertSaved(item)}>
                          <Plus /> Einfügen
                        </DropdownItem>
                        <DropdownItem onSelect={() => setRenaming(item)}>
                          <Pencil /> Umbenennen …
                        </DropdownItem>
                        <DropdownSeparator />
                        <DropdownItem danger onSelect={() => setDeleting(item)}>
                          <Trash2 /> Löschen …
                        </DropdownItem>
                      </>
                    }
                  >
                    <SavedBlockTile
                      item={item}
                      frame={frame}
                      kind={kind}
                      width={TILE_WIDTH}
                      height={TILE_HEIGHT}
                      onInsert={() => insertSaved(item)}
                      actions={
                        <>
                          <button
                            type="button"
                            title="Umbenennen"
                            onClick={() => setRenaming(item)}
                            className="rounded bg-white/90 p-1 text-zinc-600 shadow-sm hover:text-brand-700"
                          >
                            <Pencil className="size-3.5" />
                          </button>
                          <button
                            type="button"
                            title="Löschen"
                            onClick={() => setDeleting(item)}
                            className="rounded bg-white/90 p-1 text-zinc-600 shadow-sm hover:text-red-600"
                          >
                            <Trash2 className="size-3.5" />
                          </button>
                        </>
                      }
                    />
                  </ContextMenu>
                ))}
              </div>
            )}
          </section>
        ) : null}
        {found.filter(shown).map((category) => (
          <section key={category} className="space-y-2">
            <h3 className="text-xs font-semibold text-zinc-700">
              {BLOCK_CATEGORY_LABELS[category]}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {blocks
                .filter((block) => block.category === category)
                .map((block) => (
                  <LibraryBlockTile
                    key={block.id}
                    block={block}
                    buildOptions={buildOptions}
                    width={TILE_WIDTH}
                    height={TILE_HEIGHT}
                    onInsert={() => insertBlock(block)}
                  />
                ))}
            </div>
          </section>
        ))}
      </div>
      {browsing ? (
        <BlocksBrowser
          onClose={() => setBrowsing(false)}
          initialFilter={filter}
          initialQuery={query}
          blocks={allBlocks}
          savedItems={saved.items}
          buildOptions={buildOptions}
          frame={frame}
          kind={kind}
          insertBlock={insertBlock}
          insertSaved={insertSaved}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`„${deleting?.name ?? ""}“ löschen?`}
        description="Der Baustein verschwindet für alle im Team. Dateien, die ihn schon verwenden, bleiben unverändert."
        confirmLabel="Löschen"
        danger
        onConfirm={() => (deleting ? actions.remove.mutateAsync(deleting.id) : undefined)}
      />
      <NameDialog
        open={renaming !== null}
        onOpenChange={(open) => !open && setRenaming(null)}
        title="Baustein umbenennen"
        initial={renaming?.name ?? ""}
        pending={actions.rename.isPending}
        onConfirm={(name) =>
          renaming ? actions.rename.mutateAsync({ itemId: renaming.id, name }) : undefined
        }
      />
    </>
  );
}
